/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { AmazonPlaybackQuality, TrackNerdStats } from '@amzn/vinyl-amzn'
import { data } from '@amazon/vinyl-observable'
import {
    NULL_CAPTION_SOURCE,
    NULL_SECTION_NAV,
    type PlaybackError,
    type Player,
    type AmznPlaybackQualitySource,
    type CaptionSource,
} from '@/playback/api'
import type { SubPlayer } from '@/playback/SubPlayer'

/**
 * A do-nothing {@link SubPlayer} for a static render — every cell holds an idle
 * default and never changes; commands are benign no-ops (async ones resolve).
 * Imports no media code, so a headless render never touches MSE/EME or a
 * `<video>`. Also satisfies {@link AmznPlaybackQualitySource} so a delegate can expose
 * it uniformly.
 */
export function createStaticRenderSubPlayer(): SubPlayer & AmznPlaybackQualitySource {
    return {
        kind: 'vinyl',

        playing$: data(false),
        paused$: data(true),
        ended$: data(false),
        loading$: data(false),
        currentTime$: data(0),
        duration$: data(NaN),
        currentTimePercent$: data(0),
        fetchedTimePercent$: data(0),
        volume$: data(1),
        muted$: data(false),
        playbackRate$: data(1),
        loop$: data(false),
        error$: data<PlaybackError | null>(null),
        playbackQuality$: data<AmazonPlaybackQuality | null>(null),
        trackNerdStats$: data<TrackNerdStats | null>(null),
        get amznPlaybackQuality(): AmznPlaybackQualitySource {
            return this
        },
        captions: NULL_CAPTION_SOURCE as CaptionSource,
        sections: NULL_SECTION_NAV,

        play: () => Promise.resolve(),
        pause: () => {},
        seekTo: () => Promise.resolve(),
        preload: () => {},
        activate: () => Promise.resolve(),
        deactivate: () => Promise.resolve(),
        dispose: () => {},

        getQualitySelectionReason: () => null,
        setStreamingQuality: () => {},
    }
}

/**
 * A do-nothing {@link Player} used when the app boots inside a static render
 * (see the `staticPage` flag in `@/staticRender`). A headless render has no
 * interactive user and must never touch browser-only media machinery: returning
 * this stub keeps `@amzn/vinyl-amzn` unimported and the singleton `<video>`
 * element (MSE/EME) uncreated, while still satisfying the {@link Player} surface
 * the boot-time bindings type against. Every cell holds an idle default and
 * commands are benign no-ops.
 */
export function createStaticRenderPlayer(): Player {
    return createStaticRenderSubPlayer()
}
